import { useMutation } from '@apollo/client'
import { REMOVE_BASKET_ITEM } from 'library/organisms/Basket'
import { useRouter } from 'next/router'
import { BasketPresenter } from 'presenters'
import { BasketContextProps, FETCH_BASKET } from 'providers/BasketProvider'
import { HttpHeader } from 'shared/enums/httpHeader'
import { Basket_Response } from 'shared/presenters/graphqlTypes'
import useBasket from 'state/useBasket'

export const doesBasketContainCurrentActivityVoucherBooking = ({
  basket,
  activityVoucherLineItemId
}: {
  basket: BasketPresenter | undefined
  activityVoucherLineItemId: string | undefined
}) => {
  const basketLineItemIds = basket?.getLineItemIds()
  return basketLineItemIds?.includes(activityVoucherLineItemId)
}

export const removeOtherActivityVoucherBookingFromBasket = ({
  basketLineItemIds,
  removeItemFromBasket,
  activityVoucherLineItemId
}: {
  basketLineItemIds: (string | undefined)[] | undefined
  removeItemFromBasket: (remainingVoucherLineItemId: string | undefined) => void
  activityVoucherLineItemId: string | undefined
}) => {
  const remainingVoucherLineItemIds = basketLineItemIds?.filter(
    voucherBookingLineItemId =>
      voucherBookingLineItemId !== activityVoucherLineItemId
  )
  const remainingVoucherLineItemId = remainingVoucherLineItemIds?.toString()
  remainingVoucherLineItemIds?.length &&
    removeItemFromBasket(remainingVoucherLineItemId)
}

/**
 * Vouchers are added to basket when clicking "redeem" in the voucher modal and so we do not wish to clear the basket in its entirety when the shopping basket is disabled and
 * we use useBookOnClick as this would wipe the vouchers in the basket which is not desireable.
 *
 * We need to cover these scenarios:
 *
 * 1. If shopping basket disabled, basket contains line items, there are no price modifiers and current booking not from activity voucher -> clear
 * 2. If shopping basket disabled, basket contains price modifiers -> clear the line item from basket but not the price modifier
 * 3. If we are adding a new activity voucher booking and there is already a booking in basket -> clear the OTHER line item from basket
 */

export const handlePossibleBasketUpdates = ({
  shoppingBasketDisabled,
  basket,
  refetchBasket,
  removeItemFromBasket,
  activityVoucherLineItemId
}: {
  shoppingBasketDisabled: boolean
  basket?: BasketPresenter
  refetchBasket: BasketContextProps['refetchBasket']
  removeItemFromBasket: (basketLineItemId: string | undefined) => void
  activityVoucherLineItemId?: string
}) => {
  if (!basket) {
    return
  }

  const basketPriceModifiers = basket?.baskets_price_modifiers?.[0]
  const basketLineItemIds = basket?.getLineItemIds()

  if (!shoppingBasketDisabled || basketLineItemIds?.length === 0) {
    return
  }

  const basketContainsTheCurrentActivityVoucherBooking =
    doesBasketContainCurrentActivityVoucherBooking({
      basket,
      activityVoucherLineItemId
    })

  if (!activityVoucherLineItemId && !basketPriceModifiers) {
    refetchBasket({ basketId: null })
    return
  }

  if (basketPriceModifiers) {
    removeItemFromBasket(basket?.line_items?.[0]?.id)
    return
  }

  if (
    basketContainsTheCurrentActivityVoucherBooking &&
    basketLineItemIds?.length > 1
  ) {
    removeOtherActivityVoucherBookingFromBasket({
      basketLineItemIds,
      removeItemFromBasket,
      activityVoucherLineItemId
    })
    return
  }
}

const useBasketUpdate = ({
  shoppingBasketDisabled
}: {
  shoppingBasketDisabled: boolean
}) => {
  const { locale } = useRouter()
  const { basket, refetchBasket } = useBasket()
  const basketId = basket?.id

  const activityVoucherLineItemId = basket?.getActivityVoucherLineItemId()

  const [removeBasketItem] = useMutation<{
    Consumer_Baskets_RemoveItem: Basket_Response
  }>(REMOVE_BASKET_ITEM, {
    refetchQueries: [
      {
        query: FETCH_BASKET,
        variables: { basketId, locale },
        context: { headers: { [HttpHeader.xHasuraBasketId]: basketId } }
      }
    ]
  })

  const removeItemFromBasket = async (lineItemId: string | undefined) => {
    if (!basketId) {
      return
    }

    try {
      await removeBasketItem({
        variables: {
          lineItemId
        }
      })
    } catch (err) {
      // catching here to protect against page breaks
    }
  }

  handlePossibleBasketUpdates({
    shoppingBasketDisabled,
    basket,
    refetchBasket,
    removeItemFromBasket,
    activityVoucherLineItemId
  })
}

export default useBasketUpdate
